import { getPopupMaxHeight } from 'components/dropdown/DropdownPopup';
import { usePopup } from 'custom_hooks/usePopup';
import { useWindowSize } from 'custom_hooks/useWindowSize';
import { ReactNode, useMemo } from 'react';
import { QuickFilterToggle } from 'shared/filter_pane/QuickFilterToggle';
import {
    MultiSelectQuickFilterOption,
    RadioSelectQuickFilterPopup,
} from 'shared/filter_pane/RadioSelectQuickFilterPopup';
import styled from 'styled-components';

const StyledQuickFilterToggle = styled(QuickFilterToggle)<{
    $isOpen: boolean;
    isToggled: boolean;
}>`
    background-color: ${({ $isOpen, isToggled }) =>
        $isOpen && !isToggled ? 'var(--border-default)' : ''} !important;
`;

const BadgeWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const CustomBadge = styled.div<{ $textColor: string; $backgroundColor: string }>`
    display: inline-block;
    background: ${({ $backgroundColor }) => $backgroundColor};
    color: ${({ $textColor }) => $textColor};
    border-radius: 1.2rem;
    min-width: 1.4rem;
    padding: 1px 6px;
    text-align: center;
    font-size: 1.2rem;
    position: absolute;
    right: -8px;
    top: -8px;
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export type RadioSelectQuickFilterButtonProps<TValue> = {
    /**
     * Label displayed on the button
     */
    label: string;
    /**
     * Unique ID for the button element
     */
    id: string;
    /**
     * Currently selected value (undefined if none selected)
     */
    selectedValue: TValue | undefined;
    /**
     * Callback when selected value changes
     */
    onChangeValue: (value: TValue) => void;
    /**
     * All available options to display in the popup.
     * Should already be filtered and sorted as desired.
     */
    options: MultiSelectQuickFilterOption<TValue>[];
    /**
     * Optional custom badge content (if not provided, shows selected label)
     */
    badgeContent?: ReactNode;
};

export function RadioSelectQuickFilterButton<TValue>({
    label,
    id,
    selectedValue,
    onChangeValue,
    options,
    badgeContent,
}: RadioSelectQuickFilterButtonProps<TValue>) {
    useWindowSize(true);

    const { isOpen, close, toggleOpen, triggerRef, popupRef } = usePopup({
        closeOnClickOutside: true,
    });

    const adjustedMaxHeight = getPopupMaxHeight(
        triggerRef.current,
        window.innerHeight,
        false
    );

    const maxHeight = `max(8rem, min(50rem, ${adjustedMaxHeight}))`;

    const selectedLabel = useMemo(() => {
        const selectedOption = options.find((opt) => opt.value === selectedValue);
        return selectedOption?.label;
    }, [options, selectedValue]);

    const displayBadgeContent = useMemo(() => {
        return badgeContent !== undefined ? badgeContent : selectedLabel;
    }, [badgeContent, selectedLabel]);

    return (
        <>
            <BadgeWrapper>
                {selectedValue !== undefined && (
                    <CustomBadge
                        $textColor="var(--text-primary)"
                        $backgroundColor="var(--surface-raised)"
                    >
                        {displayBadgeContent}
                    </CustomBadge>
                )}
                <StyledQuickFilterToggle
                    ref={triggerRef}
                    id={id}
                    isToggled={selectedValue !== undefined}
                    onToggle={toggleOpen}
                    $isOpen={isOpen}
                    onBlur={(e) => {
                        if (
                            e.relatedTarget &&
                            e.relatedTarget !== triggerRef.current &&
                            !popupRef.current?.contains(e.relatedTarget)
                        ) {
                            close();
                        }
                    }}
                >
                    {label}
                </StyledQuickFilterToggle>
            </BadgeWrapper>
            {isOpen && (
                <RadioSelectQuickFilterPopup
                    close={close}
                    maxHeight={maxHeight}
                    selectedValue={selectedValue}
                    onChangeValue={(value) => {
                        onChangeValue(value);
                        close(); // Auto-close on selection for radio behavior
                    }}
                    popupRef={popupRef}
                    triggerRef={triggerRef}
                    options={options}
                />
            )}
        </>
    );
}
